<script setup>
import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { getI18n } from '@/plugins/i18n'
import { DxDataGrid } from 'devextreme-vue/data-grid'
import { useGridRef, useODataSource, useDefaultGridOptions, useDefaultExport, refreshGridAsync, getCreatedRowId } from '@/composables/data-grid'
import { useArchivedAtColumn, useArchivedColumn, useCompanyColumn } from '@/composables/data-grid-column'
import { useArchive } from '@/composables/archive'

import { planningApi } from '@/apis/planning.api'
import { usePlanningStore } from '@/stores/planning.store'

import ListingItemsGrid from './ListingItemsGrid.vue'
import ListingEdit from '@/components/shared/ListingEdit.vue'
import ArchiveGridButton from '@/components/shared/ArchiveGridButton.vue'
import ArchivedGridToggle from '@/components/shared/ArchivedGridToggle.vue'
import ArchiveWarningDialog from '@/components/shared/ArchiveWarningDialog.vue'

const gridId = 'listings-grid'

const i18n = getI18n()
const planningStore = usePlanningStore()
const companies = planningStore.companies.filter(i => !i.is_rental)
const editItem = ref(null)

const { gridRef } = useGridRef()
const { exportOptions, onExporting } = useDefaultExport({ fileName: i18n.t('common.listing', 2) })
const listingPresentationExport = {
  label: i18n.t('common.listing', 2),
  onClick: (selectedRowsData) => planningStore.exportListingsAsync(selectedRowsData.map(row => row.id)),
}

const gridDataSource = useODataSource(planningApi.getAbsoluteUrl() + planningApi.listingsUrl, {
  customParamsFn: () => { return showArchived.value ? { show_archived: true } : {} }
})

const { archiveWarning, archiveAsync, archiveButton, showArchived, onRowPrepared: onArchiveRowPrepared, onRowRemoving } = useArchive({
  onArchive: async(id) => { 
    await planningStore.archiveListingAsync(id) 
    await planningStore.fetchCompaniesAsync(null, true)
  },
  onDelete: async(id, config) => { await planningStore.deleteListingAsync(id, config) },
  gridRef: gridRef,
})

const route = useRoute()

// When arriving from an item (e.g. universal search), filter the primary grid down to the
// matching listing (so it is guaranteed to be loaded regardless of paging/sorting), expand it,
// and pass the item code down to the detail grid so it lands on the matched rate card item.
const detailItemCode = computed(() => route.query.itemCode || null)
let listingFilterApplied = false
let listingExpanded = false

const expandRouteListing = () => {
  const instance = gridRef.value?.instance
  const listingId = Number(route.query.listingId)
  if (!instance || !listingId) return
  instance.collapseAll(-1)
  instance.expandRow(listingId)
  listingExpanded = true
}

const onContentReady = () => {
  const instance = gridRef.value?.instance
  const listingId = Number(route.query.listingId)
  if (!instance || !listingId) return
  if (!listingFilterApplied) {
    listingFilterApplied = true
    // Setting the filter triggers a reload, which fires onContentReady again — the
    // actual expand happens on that next call, once the single matching row is loaded.
    instance.columnOption('id', 'filterValue', listingId)
    return
  }
  if (!listingExpanded) {
    expandRouteListing()
  }
}

watch(() => route.query.listingId, () => {
  listingFilterApplied = false
  listingExpanded = false
  onContentReady()
})

watch(() => route.query.itemCode, () => {
  if (listingFilterApplied) expandRouteListing()
})

const columns = [
  {
    dataField: 'id',
    caption: i18n.t('common.id'),
    visible: false,
    showInColumnChooser: false,
  },
  {
    type: 'buttons',
    showInColumnChooser: false,
    buttons: [
      {
        name: 'edit',
        visible: (e) => !e.row?.data?.archived,
        onClick: (e) => { editItem.value = { ...e.row.data } },
      },
      { name: 'delete', visible: (e) => !e.row?.data?.archived },
      { name: 'clone', icon: 'copy', visible: (e) => !e.row?.data?.archived, onClick: async(e) => {
        await planningStore.cloneListingAsync(e.row.data.id)
        await gridRef.value.instance.refresh()
      } },
      archiveButton,
    ],
    fixed: false,
    visibleIndex: -1,
    width: '8rem',
  },
  {
    dataField: 'code',
    caption: i18n.t('common.code'),
    width: '6rem',
    sortOrder: 'asc',
  },
  useCompanyColumn({ companies, sortOrder: 'asc' }),
  {
    dataField: 'name',
    caption: i18n.t('common.name'),
    validationRules: [{ type: 'required' }],
    width: '12rem',
  },
  {
    dataField: 'description',
    caption: i18n.t('common.description'),
    width: '12rem',
  },
  useArchivedAtColumn(),
  useArchivedColumn(),
]

const gridOptions = useDefaultGridOptions({
  id: gridId,
  columns: columns,
  dataSource: gridDataSource,
  editing: {
    mode: 'popup',
    allowUpdating: true,
    allowAdding: true,
    allowDeleting: true,
    useIcons: true,
  },
  selection: { mode: 'multiple', showCheckBoxesMode: 'onClick', selectAllMode: 'page' },
  onExporting: onExporting,
  export: exportOptions,
  masterDetail: {
    enabled: true,
    template: 'master-detail',
  },
  onRowPrepared: onArchiveRowPrepared,
  onRowRemoving: onRowRemoving,
  onContentReady: onContentReady,
  clearFilterButton: true,
  presentationExports: [listingPresentationExport],
  archivedToggle: true,
  addButton: {
    onClick: () => {
      editItem.value = {}
    },
  },
})

const updateGrid = async (saved) => {
  const createdId = getCreatedRowId(editItem.value, saved)
  editItem.value = null
  await refreshGridAsync(gridRef, createdId)
}

</script>
<template>
  <div>
    <archive-warning-dialog
      v-model="archiveWarning"
      @confirm="async() => await archiveAsync(archiveWarning.item.id, false)"
    />
    <q-dialog :model-value="editItem !== null" class="dialog-sm" @hide="updateGrid">
      <listing-edit
        v-if="editItem !== null"
        :model-value="editItem"
        @update="updateGrid"
        @cancel="updateGrid"
        @delete="updateGrid"
      />
    </q-dialog>
    <dx-data-grid
      ref="gridRef"
      v-bind="gridOptions"
    >
      <template #master-detail="{ data: listing }">
        <listing-items-grid
          :data="listing"
          :show-archived="showArchived"
          :code="detailItemCode"
          model
        />
      </template>
      <template #archive-button="{ data: row }">
        <archive-grid-button :row="row" />
      </template>
      <template #archived-toggle>
        <archived-grid-toggle
          :show-archived="showArchived"
          :grid-ref="gridRef"
          @update:show-archived="showArchived = $event"
        />
      </template>
    </dx-data-grid>
  </div>
</template>
